import React, { useState, useEffect } from 'react';
import logo from '../../assets/Logo/GIGiFYbasicLogo.png';
import './BookingLoadingStatePopup.css';

const BookingLoadingStatePopup = ({ 
  isVisible, 
  onClose, 
  status = 'loading', // 'loading', 'success', 'failure', 'confirmation'
  title = 'Processing Request',
  subtitle = 'Please wait while we process your request...',
  loadingNote = 'This process usually takes less than a minute.',
  successContent = null,
  failureContent = null,
  failureMessage = '', // optional specific reason shown in the default failure view
  autoCloseDelay = 8000,
  showProgress = true,
  progressDuration = 5000,
  customIcon = null,
  children = null // Allow custom content to be passed
}) => {
  const [loadingProgress, setLoadingProgress] = useState(0);

  useEffect(() => {
    if (isVisible && status === 'loading' && showProgress) {
      setLoadingProgress(0);
      
      // Simulate progress
      const startTime = Date.now();
      
      const intervalId = setInterval(() => {
        const elapsedTime = Date.now() - startTime;
        const progress = Math.min((elapsedTime / progressDuration) * 100, 95); // Stop at 95% until real completion
        setLoadingProgress(progress);

        if (elapsedTime >= progressDuration && status === 'loading') {
          // If still loading after duration, slow down progress
          setLoadingProgress(95);
        }
      }, 100);

      return () => clearInterval(intervalId);
    }
  }, [isVisible, status, showProgress, progressDuration]);

  // Complete progress when status changes to success/failure
  useEffect(() => {
    if (status === 'success' || status === 'failure') {
      setLoadingProgress(100);
    }
  }, [status]);

  // Auto-close after delay for success/failure states
  useEffect(() => {
    if ((status === 'success' || status === 'failure') && autoCloseDelay > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);

      return () => clearTimeout(timer);
    }
  }, [status, autoCloseDelay, onClose]);

  if (!isVisible) return null;

  const renderDefaultSuccessContent = () => (
    <>
      <div className="popup-icon success-icon">✓</div>
      <h3 className="popup-title success-title">Success!</h3>
      <p className="popup-subtitle">
        Your request has been processed successfully.
      </p>
      <div className="popup-next-steps">
        <h4 className="next-steps-title">Next Steps:</h4>
        <ol className="next-steps-list">
          <li>Keep an eye out for an email confirming Talent e-signature</li>
          <li>Once confirmed, you can proceed with the Booking Fee payment.</li>
        </ol>
      </div>
      <p className="popup-note success-note">
        The page will refresh automatically with your updates.
      </p>
    </>
  );

  const renderDefaultFailureContent = () => (
    <>
      <div className="popup-icon failure-icon">✕</div>
      <h3 className="popup-title failure-title">Request Failed</h3>
      <p className="popup-subtitle">
        {failureMessage || 'We encountered an issue while processing your request.'}
      </p>
      <div className="popup-next-steps">
        <h4 className="next-steps-title">What to do next:</h4>
        <ol className="next-steps-list">
          <li>Check your internet connection</li>
          <li>Refresh the page and try again</li>
          <li>Contact support if the issue persists</li>
        </ol>
      </div>
      <button 
        className="popup-retry-button"
        onClick={() => window.location.reload()}
      >
        Refresh Page
      </button>
    </>
  );

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <>
            <h3 className="popup-title">{title}</h3>
            <p className="popup-subtitle">{subtitle}</p>
            {showProgress && (
              <>
                <div className="popup-progress">
                  <div 
                    className="popup-progress-bar" 
                    style={{ width: `${loadingProgress}%` }}
                  ></div>
                  <span className="popup-progress-text">
                    {Math.round(loadingProgress)}% Complete
                  </span>
                </div>
              </>
            )}
            <p className="popup-note">{loadingNote}</p>
          </>
        );
      
      case 'success':
        return successContent || renderDefaultSuccessContent();
      
      case 'failure':
        return failureContent || renderDefaultFailureContent();
      
      case 'confirmation':
        return (
          <>
            {customIcon || <div className="popup-icon warning-icon">?</div>}
            <h3 className="popup-title">{title}</h3>
            <p className="popup-subtitle">{subtitle}</p>
            {children} {/* Allow custom content to be passed */}
          </>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="booking-popup-overlay">
      <div className="booking-popup-container">
        {/* Logo with Spinner */}
        {status === 'loading' && !customIcon ? (
          <div className="popup-logo-spinner-container">
            <img 
              src={logo} 
              alt="GIGiFY Logo" 
              className="popup-logo"
            />
            <div className="popup-spinner-circle-orange"></div>
            <div className="popup-spinner-circle-blue"></div>
          </div>
        ) : (
          <img 
            src={logo} 
            alt="GIGiFY Logo" 
            className="popup-logo"
          />
        )}

        {/* Custom Icon - show if provided */}
        {customIcon && status === 'loading' && (
          <div className="popup-custom-icon">
            {customIcon}
          </div>
        )}

        {/* Content */}
        <div className="popup-content">
          {renderContent()}
        </div>

        {/* Close button - only show for success/failure/confirmation or after reasonable time */}
        {(status !== 'loading' || loadingProgress > 90) && (
          <button 
            className="popup-close-button"
            onClick={onClose}
            aria-label="Close popup"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

export default BookingLoadingStatePopup;